import { useEffect } from 'react'
import { X, MapPin, Building2, Users, ExternalLink, Star, CalendarDays, Clock } from 'lucide-react'
import type { Posting } from '../services/api'
import { deadlineCls, formatDeadline } from './PostingsModal'

interface Props {
  posting: Posting
  saved: boolean
  onToggleSave: () => void
  onClose: () => void
}

function formatDate(value: string | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
}

export default function PostingDetailModal({ posting, saved, onToggleSave, onClose }: Props) {
  const closed = posting.status === 'CLOSED'

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center p-4">
      {/* Overlay — darker to distinguish from parent modal */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* Panel */}
      <div className="relative bg-white w-full sm:max-w-xl rounded-2xl flex flex-col max-h-[90dvh] sm:max-h-[85dvh] overflow-x-hidden shadow-2xl">

        {/* Header */}
        <div className="px-5 pt-5 pb-4 border-b border-gray-100 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <h2 className={`font-bold text-base leading-snug pr-2 ${closed ? 'text-gray-400' : 'text-gray-900'}`}>
              {posting.title}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 shrink-0 mt-0.5" aria-label="Chiudi">
              <X size={18} />
            </button>
          </div>

          {/* Ente — own row, prominent */}
          {posting.ente && (
            <p className="mt-2 text-sm font-semibold text-sky-700 leading-snug wrap-break-word">
              {posting.ente}
            </p>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

          {/* Published + Deadline row */}
          <div className="flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
              <CalendarDays size={13} className="text-gray-400" />
              {posting.published_at ? `Pubblicato il ${formatDate(posting.published_at)}` : 'Data di pubblicazione non disponibile'}
            </span>
            {closed ? (
              <span className="shrink-0 text-xs font-semibold rounded-full px-3 py-1 bg-gray-100 text-gray-400">
                Scaduto
              </span>
            ) : (
              <span className={`shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1 ${deadlineCls(posting.deadline)}`}>
                <Clock size={11} />
                {formatDeadline(posting.deadline)}
              </span>
            )}
          </div>

          {/* Meta info */}
          <div className="flex flex-col gap-2">
            {posting.locations && posting.locations.length > 0 && (
              <div className="flex items-start gap-2">
                <MapPin size={14} className="text-gray-400 mt-0.5 shrink-0" />
                <span className="text-sm text-gray-700">
                  {posting.locations.join(' · ')}
                </span>
              </div>
            )}
            {posting.positions != null && posting.positions > 0 && (
              <div className="flex items-center gap-2">
                <Users size={14} className="text-gray-400 shrink-0" />
                <span className="text-sm text-gray-700">
                  {posting.positions} {posting.positions === 1 ? 'posto disponibile' : 'posti disponibili'}
                </span>
              </div>
            )}
            {posting.sector && (
              <div className="flex items-center gap-2">
                <Building2 size={14} className="text-gray-400 shrink-0" />
                <span className="text-sm text-gray-700">{posting.sector}</span>
              </div>
            )}
            {posting.category && (
              <div className="flex items-center gap-2">
                <span className="w-3.5 shrink-0" />
                <span className="text-xs text-gray-400">{posting.category}</span>
              </div>
            )}
          </div>

          {/* Divider */}
          {posting.body && <hr className="border-gray-100" />}

          {/* Body text */}
          {posting.body && (
            <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
              {posting.body}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 shrink-0 flex items-center justify-between gap-3">
          <button
            onClick={onToggleSave}
            aria-label={saved ? 'Rimuovi dai preferiti' : 'Salva nei preferiti'}
            className={`inline-flex items-center gap-2 text-sm font-medium rounded-lg px-3 py-2 transition-colors border ${
              saved
                ? 'border-amber-200 bg-amber-50 text-amber-600 hover:bg-amber-100'
                : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-amber-500'
            }`}
          >
            <Star size={15} fill={saved ? 'currentColor' : 'none'} />
            {saved ? 'Salvato' : 'Salva'}
          </button>

          <a
            href={posting.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-sky-600 hover:bg-sky-700 rounded-lg px-4 py-2 transition-colors"
          >
            Vai al bando su inPA
            <ExternalLink size={13} />
          </a>
        </div>
      </div>
    </div>
  )
}
